<script>
import BackButton from 'dashboard/components/widgets/BackButton.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import ErrorState from 'dashboard/components/widgets/ErrorState.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import LinkBranchStaffModal from './LinkBranchStaffModal.vue';

export default {
  name: "BranchServiceDetails",
  components: {
    BackButton,
    LoadingState,
    ErrorState,
    EmptyState,
    LinkBranchStaffModal,
  },
  data() {
    return {
      serviceData: [],
      isLoading: false,
      isError: false,
      showLinkModal: false,
      promotions: [],
    }
  },
  computed: {
    serviceId() {
      return this.$route.params.serviceId;
    },
    branchId() {
      return this.$route.params.branchId;
    },
    service() {
      return this.serviceData?.service || {};
    },
    branches() {
      return this.serviceData?.branches || [];
    },
    staff() {
      return this.serviceData?.staff || [];
    },
    filteredStaff() {
      if (!this.branchId) return this.staff;
      return this.staff.filter(s =>
        s.branches?.some(b => b.id === this.branchId)
      );
    },
    stats() {
      return this.serviceData?.stats || {};
    },
    branchName() {
      const branch = this.branches.find(
        b => b.id === this.$route.params.branchId
      );

      return branch ? branch.name : 'Branch Management';
    }
  },
  methods: {
    async fetchDetails() {
      try {
        this.isLoading = true;
        this.isError = false;
        this.serviceData = await this.$store.dispatch('serviceV2/fetchServiceById', { 
          serviceId: this.serviceId, 
          branchId: this.branchId
        });
        console.log('[DEBUG BranchServiceDetails] branchId:', this.branchId, 'serviceId:', this.serviceId, 'types:', typeof this.branchId, typeof this.serviceId);
        this.promotions = await this.$store.dispatch(
          'promotions/getPromotionsForServiceInBranch',
          {
            accountId: this.$store.getters.getCurrentAccountId,
            branchId: this.branchId,
            serviceId: this.serviceId,
          }
        );
        console.log('[DEBUG BranchServiceDetails] Fetched promotions:', this.promotions);
      } catch(err) {
        console.error(err);
        this.isError = true;
      } finally {
        this.isLoading = false;
      }
    }
  },
  watch: {
    serviceId() {
      this.fetchDetails();
    },
    branchId() {
      this.fetchDetails();
    }
  },
  mounted() {
    this.fetchDetails();
  }
}
</script>

<template>
  <div class="flex-1 overflow-auto">
    <!-- Modals -->
    <LinkBranchStaffModal
      v-if="showLinkModal"
      :staffs="filteredStaff"
      @close="showLinkModal = false"
      @refresh="fetchDetails"
    />

    <!-- Header -->
    <div class="flex justify-between items-center h-14 min-h-[3.5rem] px-4 py-2 bg-white dark:bg-slate-900 border-b border-slate-50 dark:border-slate-800/50">
      <h1 class="flex items-center mb-0 text-2xl text-slate-900 dark:text-slate-100">
        <BackButton class="ml-2 mr-4" />
        <fluent-icon
          icon="building-bank"
          class="hidden ml-1 mr-2 rtl:ml-2 rtl:mr-1 md:block"
        />
        <span class="text-2xl font-medium">
          {{ branchName }} / Service / {{ service.name || 'Loading...' }}
        </span>
      </h1>
    </div>

    <!-- States -->
    <LoadingState v-if="isLoading" message="Loading service details..." />

    <ErrorState v-else-if="!isLoading && isError" />

    <EmptyState
      v-else-if="!isLoading && !isError && serviceData.length === 0"
      title="No details Found"
      message="The service details is not found"
    />

    <!-- Main Content -->
    <div v-else class="p-6">
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">

        <!-- LEFT COLUMN (2/3 width) -->
        <div class="space-y-6 lg:col-span-2">

          <!-- Service Info -->
          <div class="bg-white dark:bg-slate-800 p-4 md:p-6 rounded-lg shadow space-y-4">
            <h2 class="text-lg md:text-xl font-semibold">Service Details</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="space-y-2">
                <p><span class="font-medium">Name:</span> {{ service.name }}</p>
                <p><span class="font-medium">Description:</span> {{ service.description || '-' }}</p>
                <p><span class="font-medium">Duration:</span> {{ service.duration }} hour(s)</p>
                <p><span class="font-medium">Price:</span> {{ service.price || '-' }}</p>
                <p><span class="font-medium">Max Serve:</span> {{ service.max_serve }}</p>
                <p><span class="font-medium">Active:</span> {{ service.is_active ? 'Yes' : 'No' }}</p>
                <p v-if="service.disabled_reason"><span class="font-medium">Disabled Reason:</span> {{ service.disabled_reason }}</p>
              </div>
              <div class="flex justify-center items-center">
                <img
                  v-if="service.images[0]"
                  :src="service.images[0].presigned_url"
                  alt="Service Image"
                  class="rounded-lg shadow max-h-48 object-cover w-full"
                />
              </div>
            </div>
          </div>

          <!-- Service Images -->
          <div
            v-if="service.images.length"
            class="bg-white dark:bg-slate-800 p-4 md:p-6 rounded-lg shadow space-y-4"
          >
            <h2 class="text-lg md:text-xl font-semibold">Service Images</h2>
            <div class="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              <div
                v-for="img in service.images"
                :key="img.id"
                class="text-center"
              >
                <img
                  :src="img.presigned_url"
                  alt="Service Image"
                  class="rounded-lg shadow object-cover w-full h-32"
                />
                <p class="text-xs text-gray-500 mt-1">
                  {{ new Date(img.created_at).toLocaleDateString() }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- RIGHT COLUMN (1/3 width) -->
        <div class="space-y-6">
          <!-- <div class="bg-white dark:bg-slate-800 p-4 md:p-6 rounded-lg shadow space-y-4">
            <div class="flex justify-between items-center">
              <h2 class="text-lg md:text-xl font-semibold">Assigned Branches</h2>
              <woot-button icon="add" @click="openAsignModal">Assign</woot-button>
            </div>

            <p v-if="!serviceData.branches.length" class="text-gray-500">No branch assigned.</p>

            <ul v-else class="space-y-2 max-h-60 overflow-y-auto">
              <li
                v-for="branch in serviceData.branches"
                :key="branch.id"
                class="flex justify-between items-center p-2 border-b border-gray-200 dark:border-slate-600"
              >
                <span>{{ branch.name }}</span>
                <span
                  :class="[
                    'text-sm px-2 py-1 rounded',
                    branch.is_active
                      ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                      : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
                  ]"
                >
                  {{ branch.is_active ? 'Active' : 'Inactive' }}
                </span>
              </li>
            </ul>
          </div> -->
          <!-- Staff -->
          <div class="bg-white dark:bg-slate-800 p-4 md:p-6 rounded-lg shadow space-y-4">
            <div class="flex gap-4 justify-between">
              <h2 class="text-lg md:text-xl font-semibold">Staff</h2>
              <woot-button icon="add" @click="showLinkModal = true">Assign Staff</woot-button>
            </div>
            <p v-if="!filteredStaff.length" class="text-gray-500">No staff assigned to this branch.</p>
            <ul v-else class="space-y-2 max-h-60 overflow-y-auto">
              <li
                v-for="member in filteredStaff"
                :key="member.id"
                class="p-3 rounded-lg bg-slate-50 dark:bg-slate-700"
              >
                {{ member.name }}
              </li>
            </ul>
          </div>

          <!-- Promotions -->
          <div class="bg-white dark:bg-slate-800 p-4 md:p-6 rounded-lg shadow space-y-4">
            <h2 class="text-lg md:text-xl font-semibold">Promotions</h2>

            <p v-if="!promotions.length" class="text-gray-500">
              No promotions available for this service.
            </p>

            <ul v-else class="space-y-3 max-h-60 overflow-y-auto">
              <li
                v-for="promo in promotions"
                :key="promo.id"
                class="p-3 rounded-lg bg-slate-50 dark:bg-slate-700 space-y-1"
              >
                <div class="flex justify-between items-center">
                  <p class="font-medium">{{ promo.promotion_name }}</p>
                  <span
                    :class="[
                      'text-xs px-2 py-1 rounded',
                      promo.is_active
                        ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                        : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
                    ]"
                  >
                    {{ promo.is_active ? 'Active' : 'Inactive' }}
                  </span>
                </div>

                <p class="text-sm text-gray-600 dark:text-gray-300">
                  {{ promo.description || '-' }}
                </p>

                <p class="text-sm">
                  <span class="font-medium">Price:</span> {{ promo.price }}
                </p>

                <p v-if="promo.expired_date" class="text-xs text-gray-500">
                  Expires on {{ promo.expired_date }}
                </p>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>